import { Eye, X, Loader2 } from 'lucide-react';
import { useImpersonation } from './useImpersonation';

/**
 * Persistent "You are acting as X" banner. Mounts once near the app root and
 * renders globally while act-as is active.
 *
 * WP7 Phase 6 — this used to say "read-only", and it must never say that again:
 * act-as can now WRITE. Anything done here really happens, to that person's
 * real data, under their name in the UI (the audit trail still records who
 * actually did it). So the banner states plainly that changes are live and
 * keeps a one-click exit — the worst outcome would be someone forgetting they
 * are not themselves while making real edits.
 */
export function ImpersonationBanner() {
  const { state, stopImpersonation, isStopping } = useImpersonation();
  if (!state) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="border-fw-warning/40 bg-fw-warning-soft text-fw-warning sticky top-0 z-50 flex items-center justify-between gap-3 border-b px-4 py-2 backdrop-blur-sm"
    >
      <div className="flex items-center gap-2 text-xs sm:text-sm">
        <Eye size={14} strokeWidth={2} aria-hidden />
        <span className="font-semibold">Acting as {state.targetName}</span>
        <span className="hidden text-[11px] opacity-80 sm:inline">
          · {state.targetRole} · changes you make are live
        </span>
      </div>
      <button
        type="button"
        onClick={() => void stopImpersonation()}
        disabled={isStopping}
        className="border-fw-warning/50 bg-fw-warning-soft text-fw-warning hover:bg-fw-warning/20 -my-2 inline-flex min-h-11 items-center gap-1 rounded-full border px-3 py-1 text-xs font-semibold transition-colors"
      >
        {isStopping ? (
          <>
            <Loader2 size={11} className="animate-spin" aria-hidden /> Exiting…
          </>
        ) : (
          <>
            <X size={11} strokeWidth={2} aria-hidden /> Exit preview
          </>
        )}
      </button>
    </div>
  );
}
